import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ReceiptText, X } from "lucide-react";

import { formatFeeAdjustmentAmount } from "../../fee-adjustment/service";
import {
  feeAdjustmentSpecUnit,
  getSafeFulfillmentOrderActionErrorMessage,
  previewStructuredFeeAdjustment,
  type StructuredFeeAdjustmentValue,
} from "../service";
import type { FulfillmentOrderLine } from "../types";
import { StructuredFeeAdjustmentEditor } from "./StructuredFeeAdjustmentEditor";
import { ConfirmationButton } from "./editor/primitives";
import { confirmationDecimalInput } from "../service/confirmationDecimalDisplay";
import "./editor/adjustmentDialog.css";

type Props = {
  line: FulfillmentOrderLine | null;
  mode?: "change" | "fee-adjustment";
  isNewService?: boolean;
  initialAdjustment?: StructuredFeeAdjustmentValue;
  initialReason?: string;
  submitting?: boolean;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  formatAmount?: (value: string | null | undefined, signed?: boolean) => string;
  onSubmit: (
    line: FulfillmentOrderLine,
    adjustment: StructuredFeeAdjustmentValue,
    reason: string,
  ) => Promise<void>;
};

/** 保留服务端十进制文本，规范化与金额重算统一交给计价服务。 */
function initialValue(line: FulfillmentOrderLine, adjustment?: StructuredFeeAdjustmentValue, compact = false): StructuredFeeAdjustmentValue {
  const quantity = adjustment?.quantity ?? line.quantity;
  const billingCount = adjustment?.billingCount ?? line.billingCount;
  return {
    quantity: compact ? confirmationDecimalInput(quantity) : quantity.replace(/\.00$/u, "").replace(/(\.\d)0$/u, "$1"),
    billingCount: compact ? confirmationDecimalInput(billingCount) : billingCount.replace(/\.00$/u, "").replace(/(\.\d)0$/u, "$1"),
    specUnit: adjustment?.specUnit ?? feeAdjustmentSpecUnit(line.resourceType),
    unitPriceAmount: compact ? confirmationDecimalInput(adjustment?.unitPriceAmount ?? line.unitPriceAmount) : adjustment?.unitPriceAmount ?? line.unitPriceAmount,
  };
}

/** 已定稿确认行的结构化增减项弹窗。 */
export function FulfillmentOrderQuickAdjustmentModal({
  line,
  mode = "change",
  isNewService = false,
  initialAdjustment,
  initialReason = "",
  submitting = false,
  onClose,
  onDirtyChange,
  formatAmount,
  onSubmit,
}: Props) {
  const compactInput = Boolean(formatAmount);
  const sessionRef = useRef({ workspaceId: line?.workspaceId, lineId: line?.id, mode, generation: 0 });
  if (sessionRef.current.workspaceId !== line?.workspaceId || sessionRef.current.lineId !== line?.id || sessionRef.current.mode !== mode) {
    sessionRef.current = { workspaceId: line?.workspaceId, lineId: line?.id, mode, generation: sessionRef.current.generation + 1 };
  }
  const session = sessionRef.current;
  const mountedRef = useRef(true);
  const currentSession = () => mountedRef.current && sessionRef.current === session;
  const [value, setValue] = useState<StructuredFeeAdjustmentValue | null>(
    line ? initialValue(line, initialAdjustment, compactInput) : null,
  );
  const [reason, setReason] = useState(initialReason);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittingSession, setSubmittingSession] = useState<typeof session | null>(null);
  const submittingRef = useRef<typeof session | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const reasonId = useId();
  const validationId = useId();
  const busy = submitting || submittingSession === session;

  useEffect(() => {
    setValue(line ? initialValue(line, initialAdjustment, compactInput) : null);
    setReason(initialReason);
    setSubmitError(null);
  }, [line, initialAdjustment, initialReason, session, compactInput]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (!submitError) return;
    errorRef.current?.focus();
    errorRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [submitError]);

  const preview = useMemo(
    () => line && value ? previewStructuredFeeAdjustment(line, value) : null,
    [line, value],
  );
  const isDirty = Boolean(line && value && (
    JSON.stringify(value) !== JSON.stringify(initialValue(line, initialAdjustment, compactInput))
    || reason !== initialReason
  ));
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);
  useEffect(() => () => { onDirtyChange?.(false); }, [onDirtyChange]);
  if (!line || !value || !preview) return null;
  const validationError = isNewService && Number(value.quantity) <= 0 ? "新增服务数量必须大于零" : preview.error;
  const title = mode === "fee-adjustment" ? "确认件费用调整" : "确认件增减项";
  const close = () => { if (currentSession() && !submitting && submittingRef.current !== session) onClose(); };

  const submit = async () => {
    if (!currentSession() || submitting || submittingRef.current === session) return;
    const normalizedReason = reason.trim();
    if (validationError || !preview.normalized) {
      setSubmitError(validationError ?? "请检查调整后的计价信息");
      return;
    }
    submittingRef.current = session;
    setSubmittingSession(session);
    setSubmitError(null);
    try {
      await onSubmit(line, preview.normalized, normalizedReason);
      if (currentSession()) onClose();
    } catch (error) {
      if (currentSession()) {
        setSubmitError(getSafeFulfillmentOrderActionErrorMessage(error, "增减项提交失败，请稍后重试"));
      }
    } finally {
      if (submittingRef.current === session) submittingRef.current = null;
      if (currentSession()) setSubmittingSession(null);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) close(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="confirmation-editor-theme confirmation-overlay confirmation-adjustment-overlay" />
        <Dialog.Content
          ref={contentRef}
          className="confirmation-editor-theme confirmation-adjustment-dialog"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            (contentRef.current?.querySelector<HTMLElement>("input:not(:disabled)") ?? contentRef.current)?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus({ preventScroll: true });
          }}
        >
          <header className="confirmation-adjustment-header">
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.Description>{mode === "fee-adjustment" ? "本次先加入调整明细，完成 R3 重算后统一保存费用调整。" : "核对调整前后的费用，提交后记录至计调直接增减账本。"}</Dialog.Description>
            </div>
            <ConfirmationButton variant="ghost" size="icon" aria-label={`关闭${title}`} disabled={busy} onClick={close}><X aria-hidden /></ConfirmationButton>
          </header>
          <div className="confirmation-adjustment-body">
            <div className="confirmation-adjustment-subject">
              <ReceiptText aria-hidden />
              <div><small>{isNewService ? "新增服务" : `确认件 #${line.lineNo}`}</small><strong>{line.itemNameSnapshot}</strong>{line.itemSpecSnapshot && <span>{line.itemSpecSnapshot}</span>}</div>
              {!isNewService && <div className="confirmation-adjustment-subject-amount"><small>当前生效总额</small><strong>{(formatAmount || formatFeeAdjustmentAmount)(line.amount, false)}</strong></div>}
            </div>
            <StructuredFeeAdjustmentEditor line={line} value={value} isNewService={isNewService} disabled={busy} formatAmount={formatAmount} onChange={(next) => { setValue(next); setSubmitError(null); }} />
            {isNewService && Number(value.quantity) <= 0 ? <p className="confirmation-adjustment-error" role="alert">新增服务数量必须大于零</p> : null}
            <div className="confirmation-adjustment-reason">
              <label htmlFor={reasonId}>增减说明</label>
              <textarea id={reasonId} value={reason} maxLength={255} rows={3} disabled={busy} placeholder="填写本次费用增减原因" onChange={(event) => { setReason(event.target.value); setSubmitError(null); }} />
              <span className="confirmation-adjustment-word-count">{reason.length} / 255</span>
            </div>
            {submitError ? <p ref={errorRef} tabIndex={-1} className="confirmation-adjustment-error" role="alert">{submitError}</p> : null}
          </div>
          <footer className="confirmation-adjustment-footer">
            <p id={validationId} aria-live="polite">{busy ? "正在提交，请稍候…" : validationError ?? (mode === "fee-adjustment" ? "加入明细后可继续调整其他费用。" : "提交后保留原费用与本次增减记录。")}</p>
            <div>
              <ConfirmationButton onClick={close} disabled={busy}>取消</ConfirmationButton>
              <ConfirmationButton variant="default" loading={busy} disabled={Boolean(validationError)} aria-describedby={validationId} onClick={() => void submit()}>提交增减项</ConfirmationButton>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
